/**
 * MenuDiffService — Computes a structured diff between two menu snapshots.
 *
 * Used by the Admin "Update Requests" inbox to highlight exactly what
 * changed in a MenuChangeRequest (new items, modified fields, deletions).
 */

import type { MenuCategory, MenuItem, OptionGroup } from '../types';

export type DiffType = 'added' | 'removed' | 'modified' | 'unchanged';

export interface FieldDiff {
  field: string;
  label: string;
  oldValue: unknown;
  newValue: unknown;
}

export interface ItemDiff {
  id: string;
  nameEN: string;
  type: DiffType;
  fields: FieldDiff[];
  /** Option group changes within this item */
  groupDiffs: GroupDiff[];
}

export interface GroupDiff {
  id: string;
  nameEN: string;
  type: DiffType;
  fields: FieldDiff[];
}

export interface CategoryDiff {
  id: string;
  nameEN: string;
  type: DiffType;
  fields: FieldDiff[];
  items: ItemDiff[];
}

/** Compute full structural diff between old (live) and new (draft) category arrays */
export function computeMenuDiff(
  oldCats: MenuCategory[],
  newCats: MenuCategory[],
): CategoryDiff[] {
  const oldMap = new Map(oldCats.map(c => [c.id, c]));
  const newMap = new Map(newCats.map(c => [c.id, c]));
  const allIds = new Set([...oldMap.keys(), ...newMap.keys()]);
  const result: CategoryDiff[] = [];

  for (const id of allIds) {
    const oldCat = oldMap.get(id);
    const newCat = newMap.get(id);

    if (!oldCat && newCat) {
      result.push({ id, nameEN: newCat.nameEN, type: 'added', fields: [], items: newCat.items.map(i => itemToDiff(i, 'added')) });
    } else if (oldCat && !newCat) {
      result.push({ id, nameEN: oldCat.nameEN, type: 'removed', fields: [], items: oldCat.items.map(i => itemToDiff(i, 'removed')) });
    } else if (oldCat && newCat) {
      const fields = diffCatFields(oldCat, newCat);
      const items = diffItems(oldCat.items, newCat.items);
      const hasChanges = fields.length > 0 || items.some(i => i.type !== 'unchanged');
      result.push({ id, nameEN: newCat.nameEN, type: hasChanges ? 'modified' : 'unchanged', fields, items });
    }
  }

  return result.filter(c => c.type !== 'unchanged');
}

/** Count total changes for summary display */
export function countChanges(diffs: CategoryDiff[]): { added: number; modified: number; removed: number } {
  let added = 0, modified = 0, removed = 0;
  for (const cat of diffs) {
    if (cat.type === 'added') added++;
    else if (cat.type === 'removed') removed++;
    for (const item of cat.items) {
      if (item.type === 'added') added++;
      else if (item.type === 'removed') removed++;
      else if (item.type === 'modified') modified++;
    }
  }
  return { added, modified, removed };
}

// ── Internals ───────────────────────────────────────────────────────────────

function itemToDiff(item: MenuItem, type: DiffType): ItemDiff {
  return { id: item.id, nameEN: item.nameEN || 'Unnamed', type, fields: [], groupDiffs: [] };
}

function diffCatFields(a: MenuCategory, b: MenuCategory): FieldDiff[] {
  const diffs: FieldDiff[] = [];
  if (a.nameEN !== b.nameEN) diffs.push({ field: 'nameEN', label: 'Name (EN)', oldValue: a.nameEN, newValue: b.nameEN });
  if (a.nameAR !== b.nameAR) diffs.push({ field: 'nameAR', label: 'Name (AR)', oldValue: a.nameAR, newValue: b.nameAR });
  if (!!a.hidden !== !!b.hidden) diffs.push({ field: 'hidden', label: 'Hidden', oldValue: !!a.hidden, newValue: !!b.hidden });
  return diffs;
}

function diffItems(oldItems: MenuItem[], newItems: MenuItem[]): ItemDiff[] {
  const oldMap = new Map(oldItems.map(i => [i.id, i]));
  const newMap = new Map(newItems.map(i => [i.id, i]));
  const allIds = new Set([...oldMap.keys(), ...newMap.keys()]);
  const result: ItemDiff[] = [];

  for (const id of allIds) {
    const a = oldMap.get(id);
    const b = newMap.get(id);
    if (!a && b) {
      result.push(itemToDiff(b, 'added'));
    } else if (a && !b) {
      result.push(itemToDiff(a, 'removed'));
    } else if (a && b) {
      const fields = diffItemFields(a, b);
      const groupDiffs = diffGroups(a.optionGroups || [], b.optionGroups || []);
      const hasChanges = fields.length > 0 || groupDiffs.some(g => g.type !== 'unchanged');
      if (hasChanges) result.push({ id, nameEN: b.nameEN || 'Unnamed', type: 'modified', fields, groupDiffs });
    }
  }

  return result;
}

function diffItemFields(a: MenuItem, b: MenuItem): FieldDiff[] {
  const diffs: FieldDiff[] = [];
  if (a.nameEN !== b.nameEN) diffs.push({ field: 'nameEN', label: 'Name (EN)', oldValue: a.nameEN, newValue: b.nameEN });
  if (a.nameAR !== b.nameAR) diffs.push({ field: 'nameAR', label: 'Name (AR)', oldValue: a.nameAR, newValue: b.nameAR });
  if (a.basePrice !== b.basePrice) diffs.push({ field: 'basePrice', label: 'Price', oldValue: a.basePrice, newValue: b.basePrice });
  if ((a.descriptionEN || '') !== (b.descriptionEN || '')) diffs.push({ field: 'descriptionEN', label: 'Desc (EN)', oldValue: a.descriptionEN, newValue: b.descriptionEN });
  if ((a.descriptionAR || '') !== (b.descriptionAR || '')) diffs.push({ field: 'descriptionAR', label: 'Desc (AR)', oldValue: a.descriptionAR, newValue: b.descriptionAR });
  if (!!a.hidden !== !!b.hidden) diffs.push({ field: 'hidden', label: 'Hidden', oldValue: !!a.hidden, newValue: !!b.hidden });
  if ((a.image || '') !== (b.image || '')) diffs.push({ field: 'image', label: 'Image', oldValue: a.image ? '(has image)' : '(none)', newValue: b.image ? '(has image)' : '(none)' });
  return diffs;
}

function diffGroups(oldGrps: OptionGroup[], newGrps: OptionGroup[]): GroupDiff[] {
  const oldMap = new Map(oldGrps.map(g => [g.id, g]));
  const newMap = new Map(newGrps.map(g => [g.id, g]));
  const allIds = new Set([...oldMap.keys(), ...newMap.keys()]);
  const result: GroupDiff[] = [];

  for (const id of allIds) {
    const a = oldMap.get(id);
    const b = newMap.get(id);
    if (!a && b) {
      result.push({ id, nameEN: b.nameEN, type: 'added', fields: [] });
    } else if (a && !b) {
      result.push({ id, nameEN: a.nameEN, type: 'removed', fields: [] });
    } else if (a && b) {
      const fields: FieldDiff[] = [];
      if (a.nameEN !== b.nameEN) fields.push({ field: 'nameEN', label: 'Group Name (EN)', oldValue: a.nameEN, newValue: b.nameEN });
      if (a.nameAR !== b.nameAR) fields.push({ field: 'nameAR', label: 'Group Name (AR)', oldValue: a.nameAR, newValue: b.nameAR });
      if (a.options.length !== b.options.length) fields.push({ field: 'options', label: 'Options Count', oldValue: a.options.length, newValue: b.options.length });
      if (fields.length > 0) result.push({ id, nameEN: b.nameEN, type: 'modified', fields });
    }
  }

  return result;
}
